// ═══════════════════════════════════════════════════════════════════════
//  TECH DEMO — INDIA COVERAGE MISSION PLANNER
//  EO-1: First satellite — coverage, EP-ADCS phasing & full propellant budget
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from "react";
import {
  T, FONT, CARD, SECTION_LABEL, TH_STYLE, TD_STYLE,
  TAB_BASE, LABEL_SM, LABEL_XS,
  chip, STAT_CARD, BAR_TRACK,
} from "../theme/playbookTheme";

// ─── Physics constants ────────────────────────────────────────────────────────
const G0  = 9.80665;    // m/s²
const RE  = 6371e3;     // m
const MU  = 3.986e14;   // m³/s²
const J2  = 1.08263e-3;

// Exponential atmosphere (lookup + lerp)
function rho(alt_km) {
  const A = [200,250,300,350,400,450,500,550,600,700,800,900,1000];
  const R = [2.53e-10,6.24e-11,1.95e-11,6.98e-12,2.72e-12,1.13e-12,
             5.21e-13,2.62e-13,1.37e-13,4.17e-14,1.57e-14,6.7e-15,3.21e-15];
  if (alt_km <= A[0]) return R[0];
  if (alt_km >= A[A.length-1]) return R[R.length-1];
  for (let i = 0; i < A.length - 1; i++) {
    if (alt_km >= A[i] && alt_km <= A[i+1]) {
      const t = (alt_km - A[i]) / (A[i+1] - A[i]);
      return Math.exp(Math.log(R[i]) * (1-t) + Math.log(R[i+1]) * t);
    }
  }
  return R[R.length-1];
}

// Orbital parameters
function calcOrbit(alt_km, inc_deg) {
  const r     = RE + alt_km * 1e3;
  const v     = Math.sqrt(MU / r);
  const T_orb = 2 * Math.PI * Math.sqrt(r**3 / MU);
  const n     = 2 * Math.PI / T_orb;
  const inc   = inc_deg * Math.PI / 180;
  const dRaan_day = -1.5 * n * J2 * (RE/r)**2 * Math.cos(inc) * 86400 * (180/Math.PI);
  return { r, v, T_orb, n, dRaan_day };
}

// Rocket equation: propellant mass consumed for a ΔV given initial mass + Isp
function propMass(m_init, dv, isp) {
  if (dv <= 0 || isp <= 0 || m_init <= 0) return 0;
  return m_init * (1 - Math.exp(-dv / (isp * G0)));
}

// ─── Ground track ─────────────────────────────────────────────────────────────
function calcGroundTrack(alt_km, inc_deg, raan_deg, nOrbits = 4) {
  const { T_orb } = calcOrbit(alt_km, inc_deg);
  const steps = nOrbits * 120;
  const pts   = [];
  for (let i = 0; i <= steps; i++) {
    const t      = i * (nOrbits * T_orb / steps);
    const argLat = (2 * Math.PI * t / T_orb) % (2 * Math.PI);
    const lat    = Math.asin(Math.sin(inc_deg * Math.PI/180) * Math.sin(argLat)) * 180/Math.PI;
    const dLon   = Math.atan2(Math.cos(inc_deg * Math.PI/180) * Math.sin(argLat), Math.cos(argLat)) * 180/Math.PI;
    const lonRaw = (raan_deg + dLon - (360/86164) * t + 360) % 360;
    pts.push([lat, lonRaw > 180 ? lonRaw - 360 : lonRaw]);
  }
  return pts;
}

// Count passes + access time over India bounding box
function calcIndiaAccess(alt_km, inc_deg, raan_deg) {
  const { T_orb } = calcOrbit(alt_km, inc_deg);
  const nOrbits   = 10;
  const pts       = calcGroundTrack(alt_km, inc_deg, raan_deg, nOrbits);
  const dt_min    = nOrbits * T_orb / (nOrbits * 120) / 60;
  let passes = 0, inIndia = false, accessMin = 0;
  for (const [lat, lon] of pts) {
    const over = lat >= 8 && lat <= 37 && lon >= 68 && lon <= 97;
    if (over && !inIndia) { passes++; inIndia = true; }
    if (!over) inIndia = false;
    if (over) accessMin += dt_min;
  }
  const days = nOrbits * T_orb / 86400;
  return {
    passesPerDay:          passes / days,
    accessTimePerDayMin:   accessMin / days,
    accessTimePerPassMin:  passes > 0 ? accessMin / passes : 0,
  };
}

// EP-ADCS differential-drag phasing
function calcPhasing(alt_km, m_kg, area_m2, targetPhase_deg) {
  const { r, v, T_orb } = calcOrbit(alt_km, 97.4);
  const dens  = rho(alt_km);
  const Cd_hi = 3.0, Cd_lo = 1.5;
  const F_hi  = 0.5 * dens * Cd_hi * area_m2 * v * v;
  const F_lo  = 0.5 * dens * Cd_lo * area_m2 * v * v;
  // Semi-major axis change per orbit (energy method)
  const da_hi = -2 * F_hi * T_orb / (m_kg * 2 * Math.PI);
  const da_lo = -2 * F_lo * T_orb / (m_kg * 2 * Math.PI);
  // Period change → phase change per orbit (deg/orbit)
  const dT_hi   = 3 * Math.PI * da_hi / (Math.sqrt(MU) * Math.pow(r, -0.5));
  const dT_lo   = 3 * Math.PI * da_lo / (Math.sqrt(MU) * Math.pow(r, -0.5));
  const dPhase  = Math.abs((dT_hi - dT_lo) / T_orb * 360);
  const orbits  = Math.abs(targetPhase_deg) / (dPhase || 1e-6);
  return {
    daysNeeded:    orbits * T_orb / 86400,
    dPhasePerOrbit: dPhase,
    F_hi, F_lo,
    F_drag_nom:    0.5 * dens * 2.2 * area_m2 * v * v,
  };
}

// Drag ΔV for station keeping
function dragDV(alt_km, m_kg, area_m2, years) {
  const { v } = calcOrbit(alt_km, 97.4);
  const Fdrag  = 0.5 * rho(alt_km) * 2.2 * area_m2 * v * v;
  return (Fdrag / m_kg) * 86400 * 365.25 * years;
}

// ─── Full mission propellant budget ──────────────────────────────────────────
// Returns per-segment breakdown + totals for both propulsion architectures.
// m_wet is used as initial mass for each segment sequentially (rocket eq chain).
function calcFullBudget({
  alt_km, m_wet, years,
  dv_insertion,
  dv_attitude_yr,   // conventional only — EP-ADCS uses differential drag → 0
  dv_deorbit_conv,  // conventional deorbit
  dv_deorbit_ep,    // EP-ADCS deorbit (usually 0 — passive aero decay)
  isp_conv, isp_ep,
  area_m2, margin_pct,
}) {
  const dv_sk = dragDV(alt_km, m_wet, area_m2, years);

  const SEGS = [
    {
      label: "Orbit Insertion",
      desc:  "Launcher dispense → operational orbit",
      icon:  "🚀",
      dv_conv: dv_insertion,
      dv_ep:   dv_insertion,   // same ΔV, different Isp → less prop
    },
    {
      label: "Station Keeping",
      desc:  `Drag compensation · ${years} yr · ${alt_km} km`,
      icon:  "📡",
      dv_conv: dv_sk,
      dv_ep:   dv_sk,          // same ΔV required, EP Isp advantage
    },
    {
      label: "Attitude / Desats",
      desc:  "Reaction wheel momentum dumping",
      icon:  "🌀",
      dv_conv: dv_attitude_yr * years,
      dv_ep:   0,              // EP-ADCS uses diff-drag — zero propellant
    },
    {
      label: "Deorbit / EOL",
      desc:  "IADC <25-yr rule disposal",
      icon:  "🌍",
      dv_conv: dv_deorbit_conv,
      dv_ep:   dv_deorbit_ep,  // aero drag phasing → near-zero
    },
  ];

  // Sequential rocket equation: each burn reduces remaining mass
  let mc = m_wet, me = m_wet;
  const rows = SEGS.map(seg => {
    const pc       = propMass(mc, seg.dv_conv, isp_conv);
    const pe       = propMass(me, seg.dv_ep,   isp_ep);
    mc -= pc;
    me -= pe;
    const savings    = pc - pe;
    const savings_pct = pc > 0 ? (savings / pc) * 100 : (pe === 0 ? 100 : 0);
    return { ...seg, prop_conv: pc, prop_ep: pe, savings, savings_pct };
  });

  const sub_c  = rows.reduce((s, r) => s + r.prop_conv, 0);
  const sub_e  = rows.reduce((s, r) => s + r.prop_ep,   0);
  const mgn_c  = sub_c * margin_pct / 100;
  const mgn_e  = sub_e * margin_pct / 100;
  const tot_c  = sub_c + mgn_c;
  const tot_e  = sub_e + mgn_e;

  return {
    rows, dv_sk,
    subtotal_conv: sub_c, subtotal_ep: sub_e,
    margin_conv: mgn_c,   margin_ep: mgn_e,
    total_conv: tot_c,    total_ep: tot_e,
    total_savings: tot_c - tot_e,
    total_savings_pct: tot_c > 0 ? ((tot_c - tot_e) / tot_c) * 100 : 0,
  };
}

// ─── India SVG map helpers ────────────────────────────────────────────────────
const IND_LAT = [6, 38];
const IND_LON = [66, 99];

function ll2svg(lat, lon, W = 320, H = 340) {
  return [
    ((lon - IND_LON[0]) / (IND_LON[1] - IND_LON[0])) * W,
    H - ((lat - IND_LAT[0]) / (IND_LAT[1] - IND_LAT[0])) * H,
  ];
}

// Simplified India border polygon [lat, lon]
const INDIA_POLY = [
  [37.1,73.9],[36.8,76.5],[35.5,78.2],[34.4,78.5],[33.2,78.8],[32.0,78.3],
  [31.5,77.5],[31.5,75.5],[30.5,74.5],[29.5,73.5],[28.0,72.5],[27.0,70.5],
  [24.5,68.5],[23.0,68.2],[22.5,69.5],[22.0,71.5],[21.0,73.0],[20.0,73.5],
  [18.5,73.8],[17.5,73.5],[16.5,73.8],[15.5,74.0],[14.5,74.5],[13.0,80.2],
  [11.5,79.8],[10.5,79.5],[8.5,77.5],[8.0,77.0],[8.2,76.0],[8.5,74.5],
  [9.5,76.5],[10.5,76.0],[11.0,76.0],[12.5,80.0],[13.5,80.5],[14.5,80.5],
  [15.5,80.5],[16.5,81.0],[17.5,82.5],[18.5,83.5],[19.5,84.5],[20.5,86.5],
  [21.5,87.5],[22.5,87.5],[21.5,88.5],[22.5,89.5],[22.5,91.0],[23.5,91.5],
  [24.5,92.0],[24.5,93.5],[26.5,93.5],[27.0,95.5],[27.5,97.0],[28.0,97.5],
  [28.5,97.5],[29.5,97.5],[29.0,96.0],[28.0,95.5],[27.5,94.5],[27.5,93.0],
  [28.5,92.0],[27.5,90.5],[26.5,90.0],[27.0,89.0],[28.0,88.5],[28.5,88.0],
  [28.5,87.0],[27.5,87.0],[27.0,88.0],[26.5,87.0],[26.0,87.5],[24.5,87.0],
  [24.5,84.0],[25.5,84.0],[25.0,82.0],[24.5,80.5],[25.5,80.0],[26.5,80.0],
  [27.0,79.5],[27.5,78.5],[28.5,77.0],[29.5,75.5],[30.5,75.5],[31.5,75.0],
  [32.5,75.5],[33.5,76.5],[34.5,76.5],[35.5,77.5],[36.5,77.5],[37.1,73.9],
];

// ─── Default mission parameters ───────────────────────────────────────────────
const DEF = {
  alt_km:       500,
  inc_deg:      97.4,
  raan_deg:     45,
  m_wet:        12,
  area_m2:      0.03,
  years:        3,
  dv_insertion: 30,
  dv_attitude_yr: 2,
  dv_deorbit_conv: 0,
  dv_deorbit_ep:   0,
  isp_conv:     220,
  isp_ep:       850,
  margin_pct:   15,
  targetPhase:  180,
};

// ─── Reusable sub-components ──────────────────────────────────────────────────
function Slider({ label, value, min, max, step=1, unit="", fmt, onChange }) {
  const display = fmt ? fmt(value) : `${value}${unit}`;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom: 4 }}>
        <span style={{ fontFamily:FONT.sans, fontSize:11, color:T.t1 }}>{label}</span>
        <span style={{ fontFamily:FONT.mono, fontSize:12, color:T.acc }}>{display}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{ width:"100%", accentColor:T.acc, cursor:"pointer" }} />
    </div>
  );
}

function StatCard({ label, value, unit="", color=T.acc, sub="" }) {
  return (
    <div style={{ ...STAT_CARD, minWidth: 110, textAlign:"center" }}>
      <div style={{ ...LABEL_XS, marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily:FONT.mono, fontSize:18, fontWeight:700, color }}>
        {value}
        {unit && <span style={{ fontSize:11, fontWeight:400, color:T.t1, marginLeft:3 }}>{unit}</span>}
      </div>
      {sub && <div style={{ fontFamily:FONT.sans, fontSize:10, color:T.t2, marginTop:3 }}>{sub}</div>}
    </div>
  );
}

function SavingsBar({ labelConv, labelEP, conv, ep, color=T.acc }) {
  const max  = Math.max(conv, ep, 0.001);
  const pctC = (conv / max) * 100;
  const pctE = (ep   / max) * 100;
  return (
    <div style={{ marginBottom:10 }}>
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:3 }}>
        <span style={{ ...LABEL_SM, width:110, flexShrink:0 }}>{labelConv}</span>
        <div style={{ flex:1, ...BAR_TRACK }}>
          <div style={{ width:`${pctC}%`, height:"100%", background:T.red, borderRadius:3 }} />
        </div>
        <span style={{ fontFamily:FONT.mono, fontSize:11, color:T.red, width:52, textAlign:"right" }}>
          {conv.toFixed(3)} kg
        </span>
      </div>
      <div style={{ display:"flex", gap:8, alignItems:"center" }}>
        <span style={{ ...LABEL_SM, width:110, flexShrink:0 }}>{labelEP}</span>
        <div style={{ flex:1, ...BAR_TRACK }}>
          <div style={{ width:`${pctE}%`, height:"100%", background:color, borderRadius:3 }} />
        </div>
        <span style={{ fontFamily:FONT.mono, fontSize:11, color, width:52, textAlign:"right" }}>
          {ep.toFixed(3)} kg
        </span>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function MissionPlanner() {
  const [tab,  setTab]  = useState("overview");
  const [p,    setP]    = useState(DEF);

  const set = (key, val) => setP(prev => ({ ...prev, [key]: val }));

  // ── Derived values ──────────────────────────────────────────────────────────
  const orbit  = useMemo(() => calcOrbit(p.alt_km, p.inc_deg), [p.alt_km, p.inc_deg]);
  const access = useMemo(() => calcIndiaAccess(p.alt_km, p.inc_deg, p.raan_deg),
    [p.alt_km, p.inc_deg, p.raan_deg]);
  const phasing = useMemo(() => calcPhasing(p.alt_km, p.m_wet, p.area_m2, p.targetPhase),
    [p.alt_km, p.m_wet, p.area_m2, p.targetPhase]);
  const budget = useMemo(() => calcFullBudget(p), [p]);
  const track  = useMemo(() => calcGroundTrack(p.alt_km, p.inc_deg, p.raan_deg, 4),
    [p.alt_km, p.inc_deg, p.raan_deg]);

  // ── India SVG polyline ──────────────────────────────────────────────────────
  const indiaPts = INDIA_POLY.map(([lat,lon]) => ll2svg(lat,lon).join(",")).join(" ");
  const trackPts = track.map(([lat,lon]) => {
    if (lon < IND_LON[0] || lon > IND_LON[1]) return null;
    if (lat < IND_LAT[0] || lat > IND_LAT[1]) return null;
    return ll2svg(lat,lon).join(",");
  }).filter(Boolean).join(" ");

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const f1  = v => v.toFixed(1);
  const f2  = v => v.toFixed(2);
  const f3  = v => v.toFixed(3);
  const pct = v => `${v.toFixed(1)}%`;

  const TABS = [
    { id:"overview",  label:"Mission Overview" },
    { id:"coverage",  label:"Coverage & Passes" },
    { id:"phasing",   label:"EP-ADCS Phasing" },
    { id:"propellant",label:"Propellant Budget" },
  ];

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily:FONT.sans, color:T.t0, maxWidth:1100, margin:"0 auto", padding:"24px 16px" }}>

      {/* ── Page header ── */}
      <div style={{ marginBottom:24 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
          <span style={{ fontSize:22 }}>🛰</span>
          <h1 style={{ margin:0, fontSize:20, fontWeight:700, color:T.t0 }}>
            Tech Demo — India Coverage Mission
          </h1>
          <span style={{ ...chip(T.grn), marginLeft:4 }}>EO-1</span>
        </div>
        <p style={{ margin:0, fontSize:12, color:T.t1 }}>
          First satellite · EP-ADCS differential drag · SSO 500 km · India/South Asia coverage
        </p>
      </div>

      {/* ── Top stat bar ── */}
      <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:20 }}>
        <StatCard label="Altitude"   value={p.alt_km}    unit="km"   />
        <StatCard label="Period"     value={f1(orbit.T_orb/60)} unit="min" />
        <StatCard label="Velocity"   value={f2(orbit.v/1000)}   unit="km/s" />
        <StatCard label="Passes/day" value={f1(access.passesPerDay)} color={T.grn} />
        <StatCard label="Access/day" value={f1(access.accessTimePerDayMin)} unit="min" color={T.grn} />
        <StatCard label="RAAN drift" value={f2(orbit.dRaan_day)} unit="°/day" color={T.yel} />
        <StatCard label="Prop saved" value={f1(budget.total_savings)} unit="kg" color={T.pur}
          sub={pct(budget.total_savings_pct)} />
      </div>

      {/* ── Tab bar ── */}
      <div style={{ display:"flex", borderBottom:`1px solid ${T.b1}`, marginBottom:20, overflowX:"auto" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{
              ...TAB_BASE,
              color: tab === t.id ? T.acc : T.t1,
              borderBottom: tab === t.id ? `2px solid ${T.acc}` : "2px solid transparent",
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════
          TAB: MISSION OVERVIEW
      ══════════════════════════════════════════════════════════ */}
      {tab === "overview" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

          {/* Orbital parameters */}
          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>Orbital Parameters</div>
            <Slider label="Altitude"      value={p.alt_km}   min={300} max={700} step={10} unit=" km"
              onChange={v => set("alt_km", v)} />
            <Slider label="Inclination"   value={p.inc_deg}  min={0}   max={120} step={0.1}
              fmt={v => `${v.toFixed(1)}°`} onChange={v => set("inc_deg", v)} />
            <Slider label="RAAN"          value={p.raan_deg} min={0}   max={360} step={1}
              fmt={v => `${v}°`} onChange={v => set("raan_deg", v)} />
            <Slider label="Wet mass"      value={p.m_wet}    min={1}   max={100} step={0.5} unit=" kg"
              onChange={v => set("m_wet", v)} />
            <Slider label="Drag area"     value={p.area_m2}  min={0.005} max={0.2} step={0.005}
              fmt={v => `${v.toFixed(3)} m²`} onChange={v => set("area_m2", v)} />
            <Slider label="Mission life"  value={p.years}    min={1}   max={7}   step={0.5} unit=" yr"
              onChange={v => set("years", v)} />
          </div>

          {/* Derived orbital facts */}
          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>Orbital Facts</div>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
              <tbody>
                {[
                  ["Semi-major axis",  `${((orbit.r)/1000).toFixed(1)} km`],
                  ["Orbital period",   `${f1(orbit.T_orb/60)} min`],
                  ["Orbital velocity", `${f2(orbit.v/1000)} km/s`],
                  ["Orbits/day",       `${f2(86400/orbit.T_orb)}`],
                  ["RAAN precession",  `${f2(orbit.dRaan_day)}°/day`],
                  ["SSO condition",    `${f2(-0.9856)}°/day (target)`],
                  ["Drag force",       `${(0.5*rho(p.alt_km)*2.2*p.area_m2*(orbit.v**2)*1e6).toFixed(2)} μN`],
                  ["Drag accel",       `${((0.5*rho(p.alt_km)*2.2*p.area_m2*(orbit.v**2)/p.m_wet)*1e6).toFixed(3)} μm/s²`],
                  ["SK ΔV total",      `${f2(budget.dv_sk)} m/s`],
                  ["SK ΔV/year",       `${f2(budget.dv_sk/p.years)} m/s/yr`],
                ].map(([k,v]) => (
                  <tr key={k}>
                    <td style={{ ...TD_STYLE, color:T.t1 }}>{k}</td>
                    <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right" }}>{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* India access summary */}
          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>India Coverage Summary</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
              <StatCard label="Passes/day"     value={f1(access.passesPerDay)} color={T.grn} />
              <StatCard label="Access/day"     value={f1(access.accessTimePerDayMin)} unit="min" color={T.grn} />
              <StatCard label="Per-pass time"  value={f1(access.accessTimePerPassMin)} unit="min" />
              <StatCard label="Annual passes"  value={Math.round(access.passesPerDay*365)} color={T.acc} />
              <StatCard label="Annual access"  value={f1(access.accessTimePerDayMin*365/60)} unit="hr/yr" />
              <StatCard label="Duty cycle"     value={f1(access.accessTimePerDayMin/1440*100)} unit="%" color={T.yel} />
            </div>
          </div>

          {/* Mission description */}
          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>Mission Description</div>
            <div style={{ fontSize:12, color:T.t1, lineHeight:1.7 }}>
              <p style={{ margin:"0 0 10px" }}>
                <span style={{ color:T.t0, fontWeight:600 }}>EO-1</span> is SPACEMANCRAFT's
                first Earth observation satellite targeting India and South Asia.
                It demonstrates EP-ADCS differential-drag technology for passive
                attitude control and orbital phasing — eliminating reaction wheels
                and conventional thruster desaturation burns.
              </p>
              <p style={{ margin:"0 0 10px" }}>
                <span style={{ color:T.acc }}>Orbit:</span> Sun-synchronous {p.alt_km} km
                · {p.inc_deg}° inclination · RAAN {p.raan_deg}°
              </p>
              <p style={{ margin:"0 0 10px" }}>
                <span style={{ color:T.grn }}>Coverage:</span> India bounding box
                (8–37°N, 68–97°E) · {f1(access.passesPerDay)} passes/day ·
                {f1(access.accessTimePerPassMin)} min/pass average
              </p>
              <p style={{ margin:0 }}>
                <span style={{ color:T.pur }}>Propellant savings:</span> EP-ADCS Isp {p.isp_ep} s
                vs conventional {p.isp_conv} s saves {f1(budget.total_savings)} kg
                ({pct(budget.total_savings_pct)}) across all mission operations.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB: COVERAGE & PASSES
      ══════════════════════════════════════════════════════════ */}
      {tab === "coverage" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr auto", gap:16 }}>

          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>India Ground Track</div>

            {/* SVG map */}
            <div style={{ background:T.bg0, borderRadius:6, padding:8, marginBottom:16, display:"inline-block" }}>
              <svg width={320} height={340} style={{ display:"block" }}>
                {/* Grid lines */}
                {[10,20,30].map(lat => {
                  const [, y] = ll2svg(lat, 68);
                  return <line key={lat} x1={0} y1={y} x2={320} y2={y}
                    stroke={T.b0} strokeWidth={0.5} />;
                })}
                {[70,80,90].map(lon => {
                  const [x] = ll2svg(10, lon);
                  return <line key={lon} x1={x} y1={0} x2={x} y2={340}
                    stroke={T.b0} strokeWidth={0.5} />;
                })}

                {/* India boundary */}
                <polygon points={indiaPts} fill={`${T.acc}15`}
                  stroke={T.acc} strokeWidth={1.2} />

                {/* Ground track */}
                {trackPts && (
                  <polyline points={trackPts}
                    fill="none" stroke={T.grn} strokeWidth={1.5} strokeDasharray="4 2" />
                )}

                {/* City dots */}
                {[
                  [28.6,"Delhi",77.2],[19.0,"Mumbai",72.8],[13.1,"Bengaluru",77.6],
                  [22.5,"Kolkata",88.3],[17.4,"Hyderabad",78.5],
                ].map(([lat,name,lon]) => {
                  const [x,y] = ll2svg(lat,lon);
                  return (
                    <g key={name}>
                      <circle cx={x} cy={y} r={3} fill={T.yel} />
                      <text x={x+5} y={y+4} fontSize={9} fill={T.t1}>{name}</text>
                    </g>
                  );
                })}

                {/* Labels */}
                <text x={4} y={12}  fontSize={9} fill={T.t2}>37°N</text>
                <text x={4} y={338} fontSize={9} fill={T.t2}>6°N</text>
                <text x={2} y={340} fontSize={9} fill={T.t2}>66°E</text>
                <text x={285} y={340} fontSize={9} fill={T.t2}>99°E</text>
              </svg>
            </div>

            <div style={{ fontSize:11, color:T.t1 }}>
              Dashed green = ground track · Blue fill = India coverage zone ·
              Track shown for {p.inc_deg}° inclination, RAAN {p.raan_deg}°
            </div>
          </div>

          {/* Access table */}
          <div style={{ minWidth:280 }}>
            <div style={{ ...CARD, marginBottom:12 }}>
              <div style={SECTION_LABEL}>Pass Statistics</div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <tbody>
                  {[
                    ["Passes / day",        `${f1(access.passesPerDay)}`],
                    ["Access time / day",   `${f1(access.accessTimePerDayMin)} min`],
                    ["Access per pass",     `${f1(access.accessTimePerPassMin)} min`],
                    ["Passes / year",       `${Math.round(access.passesPerDay*365)}`],
                    ["Annual access",       `${f1(access.accessTimePerDayMin*365/60)} hr`],
                    ["Coverage duty cycle", `${f1(access.accessTimePerDayMin/1440*100)}%`],
                  ].map(([k,v]) => (
                    <tr key={k}>
                      <td style={{ ...TD_STYLE, fontSize:11, color:T.t1 }}>{k}</td>
                      <td style={{ ...TD_STYLE, fontSize:12, fontFamily:FONT.mono, textAlign:"right" }}>{v}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ ...CARD }}>
              <div style={SECTION_LABEL}>Imaging Windows</div>
              <div style={{ fontSize:12, color:T.t1, lineHeight:1.7 }}>
                <div style={{ marginBottom:6 }}>
                  <span style={{ ...chip(T.grn) }}>GOOD</span>
                  <span style={{ marginLeft:8 }}>Sun elev &gt;30° — shadow-free imaging</span>
                </div>
                <div style={{ marginBottom:6 }}>
                  <span style={{ ...chip(T.yel) }}>MARGINAL</span>
                  <span style={{ marginLeft:8 }}>Sun elev 10–30° — glint risk</span>
                </div>
                <div>
                  <span style={{ ...chip(T.red) }}>NIGHT</span>
                  <span style={{ marginLeft:8 }}>Sub-solar pass, IR/SAR only</span>
                </div>
                <div style={{ marginTop:12, fontSize:11 }}>
                  SSO {p.inc_deg}° maintains near-constant local solar time
                  at the ascending node → consistent illumination geometry
                  across India on every revisit.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB: EP-ADCS PHASING
      ══════════════════════════════════════════════════════════ */}
      {tab === "phasing" && (
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>

          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>Phasing Parameters</div>
            <Slider label="Target phase angle" value={p.targetPhase} min={1} max={360} step={1}
              fmt={v => `${v}°`} onChange={v => set("targetPhase", v)} />
            <Slider label="Altitude"    value={p.alt_km}  min={300} max={700} step={10} unit=" km"
              onChange={v => set("alt_km", v)} />
            <Slider label="Wet mass"    value={p.m_wet}   min={1}   max={100} step={0.5} unit=" kg"
              onChange={v => set("m_wet", v)} />
            <Slider label="Drag area"   value={p.area_m2} min={0.005} max={0.2} step={0.005}
              fmt={v => `${v.toFixed(3)} m²`} onChange={v => set("area_m2", v)} />

            <div style={{ marginTop:16 }}>
              <div style={SECTION_LABEL}>Phasing Results</div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                <StatCard label="Days to phase"  value={f1(phasing.daysNeeded)} unit="d" color={T.grn} />
                <StatCard label="Phase/orbit"    value={f2(phasing.dPhasePerOrbit)} unit="°/orbit" />
                <StatCard label="F drag (hi Cd)" value={(phasing.F_hi*1e6).toFixed(1)} unit="μN" color={T.red} />
                <StatCard label="F drag (lo Cd)" value={(phasing.F_lo*1e6).toFixed(1)} unit="μN" color={T.acc} />
              </div>
            </div>
          </div>

          <div style={{ ...CARD }}>
            <div style={SECTION_LABEL}>EP-ADCS Phasing Principle</div>
            <div style={{ fontSize:12, color:T.t1, lineHeight:1.75 }}>
              <p style={{ margin:"0 0 10px" }}>
                EP-ADCS modulates the satellite's drag coefficient between
                <span style={{ color:T.red, fontFamily:FONT.mono }}> Cd≈3.0</span> (panels perpendicular)
                and <span style={{ color:T.grn, fontFamily:FONT.mono }}> Cd≈1.5</span> (edge-on).
                The asymmetric drag changes the orbital period on the high-drag arc,
                creating a cumulative phase shift — <span style={{ fontWeight:600, color:T.t0 }}>zero propellant consumed</span>.
              </p>
              <p style={{ margin:"0 0 10px" }}>
                <span style={{ color:T.acc }}>Day/night density asymmetry</span> (SSO orbits) further
                amplifies the net phase drift by creating different drag environments on
                ascending vs descending arcs.
              </p>
              <p style={{ margin:0 }}>
                At {p.alt_km} km with {p.area_m2} m² area, phasing by{" "}
                <span style={{ color:T.pur, fontFamily:FONT.mono }}>{p.targetPhase}°</span> takes{" "}
                <span style={{ color:T.grn, fontFamily:FONT.mono }}>{f1(phasing.daysNeeded)} days</span> —
                no propellant, no hardware actuators.
              </p>
            </div>

            <div style={{ marginTop:16 }}>
              <div style={SECTION_LABEL}>Phasing Table</div>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    <th style={{ ...TH_STYLE }}>Phase target</th>
                    <th style={{ ...TH_STYLE, textAlign:"right" }}>Days</th>
                    <th style={{ ...TH_STYLE, textAlign:"right" }}>Propellant</th>
                  </tr>
                </thead>
                <tbody>
                  {[45,90,120,180,270,360].map(deg => {
                    const { daysNeeded } = calcPhasing(p.alt_km, p.m_wet, p.area_m2, deg);
                    return (
                      <tr key={deg} style={{ background: deg === p.targetPhase ? `${T.acc}10` : "transparent" }}>
                        <td style={{ ...TD_STYLE }}>{deg}°</td>
                        <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.grn }}>
                          {f1(daysNeeded)} d
                        </td>
                        <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.grn }}>
                          0 kg
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ fontSize:10, color:T.t2, marginTop:6 }}>
                Conventional thruster equivalent: ~{f2(propMass(p.m_wet, orbit.v * p.targetPhase/360/100, p.isp_conv)*1000)} g at Isp {p.isp_conv} s
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          TAB: PROPELLANT BUDGET  (full mission ΔV breakdown)
      ══════════════════════════════════════════════════════════ */}
      {tab === "propellant" && (
        <div style={{ display:"grid", gridTemplateColumns:"340px 1fr", gap:16 }}>

          {/* ── Left: inputs ── */}
          <div>
            <div style={{ ...CARD, marginBottom:12 }}>
              <div style={SECTION_LABEL}>Mission Parameters</div>
              <Slider label="Mission life"   value={p.years}   min={1} max={10} step={0.5} unit=" yr"
                onChange={v => set("years", v)} />
              <Slider label="Wet mass"       value={p.m_wet}   min={1} max={200} step={1} unit=" kg"
                onChange={v => set("m_wet", v)} />
              <Slider label="Altitude"       value={p.alt_km}  min={300} max={800} step={10} unit=" km"
                onChange={v => set("alt_km", v)} />
              <Slider label="Drag area"      value={p.area_m2} min={0.005} max={0.5} step={0.005}
                fmt={v => `${v.toFixed(3)} m²`} onChange={v => set("area_m2", v)} />
            </div>

            <div style={{ ...CARD, marginBottom:12 }}>
              <div style={SECTION_LABEL}>ΔV Budget Inputs</div>
              <Slider label="Insertion ΔV"   value={p.dv_insertion}     min={0}  max={300} step={1}
                fmt={v=>`${v} m/s`} onChange={v => set("dv_insertion", v)} />
              <Slider label="Attitude ΔV/yr" value={p.dv_attitude_yr}   min={0}  max={15}  step={0.1}
                fmt={v=>`${v.toFixed(1)} m/s/yr (conv only)`} onChange={v => set("dv_attitude_yr", v)} />
              <Slider label="Deorbit ΔV (conv)" value={p.dv_deorbit_conv} min={0} max={100} step={1}
                fmt={v=>`${v} m/s`} onChange={v => set("dv_deorbit_conv", v)} />
              <Slider label="Deorbit ΔV (EP-ADCS)" value={p.dv_deorbit_ep} min={0} max={50} step={0.5}
                fmt={v=>`${v} m/s`} onChange={v => set("dv_deorbit_ep", v)} />
            </div>

            <div style={{ ...CARD }}>
              <div style={SECTION_LABEL}>Propulsion Inputs</div>
              <Slider label="Conv Isp"   value={p.isp_conv}    min={50}  max={500} step={5}
                fmt={v=>`${v} s`} onChange={v => set("isp_conv", v)} />
              <Slider label="EP-ADCS Isp" value={p.isp_ep}     min={200} max={3000} step={10}
                fmt={v=>`${v} s`} onChange={v => set("isp_ep", v)} />
              <Slider label="Contingency margin" value={p.margin_pct} min={0} max={30} step={1}
                fmt={v=>`${v}%`} onChange={v => set("margin_pct", v)} />
            </div>
          </div>

          {/* ── Right: budget breakdown ── */}
          <div>

            {/* Summary headline cards */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:14 }}>
              <StatCard label="Conv total"     value={f3(budget.total_conv)}    unit="kg" color={T.red} />
              <StatCard label="EP-ADCS total"  value={f3(budget.total_ep)}      unit="kg" color={T.grn} />
              <StatCard label="Total savings"  value={f3(budget.total_savings)} unit="kg" color={T.pur}
                sub={pct(budget.total_savings_pct)} />
              <StatCard label="Isp ratio"      value={f2(p.isp_ep/p.isp_conv)} unit="×" color={T.acc} />
            </div>

            {/* Per-segment breakdown table */}
            <div style={{ ...CARD, marginBottom:12 }}>
              <div style={SECTION_LABEL}>Full Mission ΔV Budget — Per-Segment Breakdown</div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse", minWidth:580 }}>
                  <thead>
                    <tr>
                      <th style={{ ...TH_STYLE }}>Budget item</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>ΔV (conv)</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>ΔV (EP)</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>Conv prop</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>EP prop</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>Savings</th>
                      <th style={{ ...TH_STYLE, textAlign:"right" }}>Savings %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budget.rows.map((row, i) => {
                      const savColor = row.savings_pct >= 80 ? T.grn
                        : row.savings_pct >= 30 ? T.acc : T.yel;
                      const dvConv = i === 0 ? p.dv_insertion
                        : i === 1 ? budget.dv_sk
                        : i === 2 ? p.dv_attitude_yr * p.years
                        : p.dv_deorbit_conv;
                      const dvEP = i === 0 ? p.dv_insertion
                        : i === 1 ? budget.dv_sk
                        : i === 2 ? 0
                        : p.dv_deorbit_ep;
                      return (
                        <tr key={row.label} style={{ borderBottom:`1px solid ${T.b0}` }}>
                          <td style={{ ...TD_STYLE }}>
                            <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                              <span>{row.icon}</span>
                              <div>
                                <div style={{ fontWeight:600, color:T.t0 }}>{row.label}</div>
                                <div style={{ fontSize:10, color:T.t2 }}>{row.desc}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", fontSize:11, color:T.t1 }}>
                            {f2(dvConv)} m/s
                          </td>
                          <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", fontSize:11, color:T.t1 }}>
                            {f2(dvEP)} m/s
                          </td>
                          <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.red }}>
                            {f3(row.prop_conv)} kg
                          </td>
                          <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.grn }}>
                            {f3(row.prop_ep)} kg
                          </td>
                          <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:savColor }}>
                            {f3(row.savings)} kg
                          </td>
                          <td style={{ ...TD_STYLE, textAlign:"right" }}>
                            <span style={{ ...chip(savColor) }}>
                              {row.savings_pct >= 99.9 ? "100%" : pct(row.savings_pct)}
                            </span>
                          </td>
                        </tr>
                      );
                    })}

                    {/* Subtotal */}
                    <tr style={{ background:`${T.b0}`, borderTop:`1px solid ${T.b1}` }}>
                      <td style={{ ...TD_STYLE, color:T.t1, fontSize:11 }} colSpan={3}>Subtotal (pre-margin)</td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.red }}>
                        {f3(budget.subtotal_conv)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.grn }}>
                        {f3(budget.subtotal_ep)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.pur }}>
                        {f3(budget.subtotal_conv - budget.subtotal_ep)} kg
                      </td>
                      <td style={{ ...TD_STYLE, textAlign:"right" }}>
                        <span style={{ ...chip(T.pur) }}>
                          {pct((budget.subtotal_conv-budget.subtotal_ep)/Math.max(budget.subtotal_conv,0.001)*100)}
                        </span>
                      </td>
                    </tr>

                    {/* Contingency */}
                    <tr>
                      <td style={{ ...TD_STYLE, color:T.t1, fontSize:11 }} colSpan={3}>
                        Contingency (+{p.margin_pct}%)
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.yel, fontSize:11 }}>
                        +{f3(budget.margin_conv)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.yel, fontSize:11 }}>
                        +{f3(budget.margin_ep)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, textAlign:"right", color:T.yel, fontSize:11 }}>
                        {f3(budget.margin_conv - budget.margin_ep)} kg
                      </td>
                      <td style={{ ...TD_STYLE }} />
                    </tr>

                    {/* Grand total */}
                    <tr style={{ background:`${T.acc}08`, borderTop:`2px solid ${T.b1}` }}>
                      <td style={{ ...TD_STYLE, fontWeight:700, color:T.t0 }} colSpan={3}>
                        TOTAL PROPELLANT REQUIRED
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, fontWeight:700, textAlign:"right", color:T.red, fontSize:14 }}>
                        {f3(budget.total_conv)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, fontWeight:700, textAlign:"right", color:T.grn, fontSize:14 }}>
                        {f3(budget.total_ep)} kg
                      </td>
                      <td style={{ ...TD_STYLE, fontFamily:FONT.mono, fontWeight:700, textAlign:"right", color:T.pur, fontSize:14 }}>
                        {f3(budget.total_savings)} kg
                      </td>
                      <td style={{ ...TD_STYLE, textAlign:"right" }}>
                        <span style={{ ...chip(T.pur), fontSize:12, padding:"3px 10px" }}>
                          {pct(budget.total_savings_pct)}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Visual comparison bars per segment */}
            <div style={{ ...CARD, marginBottom:12 }}>
              <div style={SECTION_LABEL}>Per-Segment Propellant Comparison</div>
              {budget.rows.map(row => (
                <div key={row.label} style={{ marginBottom:14 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                    <span>{row.icon}</span>
                    <span style={{ fontFamily:FONT.sans, fontSize:11, fontWeight:600, color:T.t0 }}>
                      {row.label}
                    </span>
                    <span style={{ ...chip(
                      row.savings_pct >= 80 ? T.grn : row.savings_pct >= 30 ? T.acc : T.yel
                    ), marginLeft:"auto" }}>
                      {row.savings_pct >= 99.9 ? "100% saved" : `${pct(row.savings_pct)} saved`}
                    </span>
                  </div>
                  <SavingsBar
                    labelConv={`Conv (Isp ${p.isp_conv}s)`}
                    labelEP={`EP-ADCS (Isp ${p.isp_ep}s)`}
                    conv={row.prop_conv}
                    ep={row.prop_ep}
                    color={T.grn}
                  />
                </div>
              ))}
              <div style={{ borderTop:`1px solid ${T.b1}`, paddingTop:12 }}>
                <SavingsBar
                  labelConv="Conv TOTAL"
                  labelEP="EP-ADCS TOTAL"
                  conv={budget.total_conv}
                  ep={budget.total_ep}
                  color={T.pur}
                />
              </div>
            </div>

            {/* Notes */}
            <div style={{ ...CARD }}>
              <div style={SECTION_LABEL}>Budget Notes</div>
              <div style={{ fontSize:11, color:T.t1, lineHeight:1.8 }}>
                <div>🚀 <b style={{ color:T.t0 }}>Orbit Insertion</b> — same ΔV for both; EP-ADCS higher Isp → less mass burned.</div>
                <div>📡 <b style={{ color:T.t0 }}>Station Keeping</b> — drag ΔV = {f2(budget.dv_sk)} m/s over {p.years} yr; auto-computed from density at {p.alt_km} km.</div>
                <div>🌀 <b style={{ color:T.t0 }}>Attitude / Desats</b> — conventional: {f2(p.dv_attitude_yr)} m/s/yr thruster burns for wheel desaturation.
                  EP-ADCS uses differential drag for attitude → <span style={{ color:T.grn }}>zero propellant</span>.</div>
                <div>🌍 <b style={{ color:T.t0 }}>Deorbit / EOL</b> — at {p.alt_km} km, natural re-entry
                  {p.alt_km <= 600 ? " occurs within 25 yr (IADC compliant) — set both to 0 m/s for passive compliance." :
                    " exceeds 25 yr — active deorbit burn or EP-ADCS drag phasing required."}</div>
                <div style={{ marginTop:8, color:T.t2 }}>
                  Rocket equation applied sequentially: each segment reduces remaining mass for next segment.
                  Contingency of {p.margin_pct}% added post-subtotal.
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
